import { useMemo } from "react"
import { Storefront, Warehouse } from "@phosphor-icons/react"
import {
  Background,
  Controls,
  Handle,
  MiniMap,
  Position,
  ReactFlow,
  type Edge,
  type Node,
  type NodeProps,
} from "@xyflow/react"

import "@xyflow/react/dist/style.css"

import { Badge } from "~/components/design-system/components"
import { cn } from "~/utils/helpers"
import { useRouteGraph, type RouteGraph } from "../useAllocationData"

const WAREHOUSE_X = 0
const STORE_X = 380
const ROW_HEIGHT = 64
const UNROUTED_GAP = 2

type WarehouseNodeData = { name: string; storeCount: number }
type StoreNodeData = {
  name: string
  country: string | null
  blockId: string | null
  source: string | null
  packageName: string | null
}

function WarehouseNode({ data }: NodeProps<Node<WarehouseNodeData>>) {
  const unrouted = data.storeCount === 0
  return (
    <div
      className={cn(
        "w-64 rounded-lg border bg-component px-3 py-2 shadow-card-rest",
        unrouted ? "border-dashed border-badge-orange-border" : "border-strong",
      )}
    >
      <div className="flex items-center gap-2">
        <Warehouse size={16} className="text-fg-subtle" />
        <span className="label-base truncate text-fg-base">{data.name}</span>
      </div>
      <div className={cn("paragraph-xs", unrouted ? "text-badge-orange-fg-text" : "text-fg-muted")}>
        {unrouted
          ? "supplies no stores"
          : `${data.storeCount} store${data.storeCount === 1 ? "" : "s"}`}
      </div>
      <Handle type="source" position={Position.Right} />
    </div>
  )
}

function StoreNode({ data }: NodeProps<Node<StoreNodeData>>) {
  const unrouted = data.source === null
  return (
    <div
      className={cn(
        "w-72 rounded-lg border bg-base px-3 py-2 shadow-card-rest",
        unrouted ? "border-dashed border-badge-orange-border" : "border-base",
      )}
    >
      <div className="flex items-center gap-2">
        <Storefront size={16} className="text-fg-subtle" />
        <span className="label-base truncate text-fg-base">{data.name}</span>
        {data.country && (
          <span className="paragraph-xs shrink-0 text-fg-muted">{data.country}</span>
        )}
      </div>
      <div className="mt-1 flex items-center gap-1.5">
        {unrouted ? (
          <span className="paragraph-xs text-badge-orange-fg-text">no warehouse supplies it</span>
        ) : (
          <>
            <Badge variant={data.source === "ingested" ? "default" : "emphasizedGreen"} size="xs">
              {data.source}
            </Badge>
            {data.packageName ? (
              <Badge variant="default" size="xs">
                {data.packageName}
              </Badge>
            ) : (
              <span className="paragraph-xs text-fg-muted">no package</span>
            )}
          </>
        )}
        {data.blockId && <span className="paragraph-xs text-fg-muted">{data.blockId}</span>}
      </div>
      <Handle type="target" position={Position.Left} />
    </div>
  )
}

const nodeTypes = { warehouse: WarehouseNode, store: StoreNode }

export function buildGraph(graph: RouteGraph): { nodes: Node[]; edges: Edge[] } {
  const routesByWarehouse = new Map<string, RouteGraph["routes"]>()
  const routeByStore = new Map<string, RouteGraph["routes"][number]>()
  for (const route of graph.routes) {
    const group = routesByWarehouse.get(route.warehouseId)
    if (group) group.push(route)
    else routesByWarehouse.set(route.warehouseId, [route])
    routeByStore.set(route.storeId, route)
  }
  const storeById = new Map(graph.stores.map((store) => [store.id, store]))

  const nodes: Node[] = []
  const edges: Edge[] = []
  let row = 0

  const storeNode = (store: RouteGraph["stores"][number], source: string | null) => ({
    id: `store-${store.id}`,
    type: "store",
    position: { x: STORE_X, y: row * ROW_HEIGHT },
    data: {
      name: store.name,
      country: store.country,
      blockId: store.blockId,
      source,
      packageName: source === null ? null : (routeByStore.get(store.id)?.packageName ?? null),
    },
  })

  for (const warehouse of graph.warehouses) {
    const group = routesByWarehouse.get(warehouse.id) ?? []
    const firstRow = row
    for (const route of group) {
      const store = storeById.get(route.storeId)
      if (!store) continue
      nodes.push(storeNode(store, route.source))
      edges.push({
        id: route.id,
        source: `warehouse-${warehouse.id}`,
        target: `store-${store.id}`,
        animated: route.source === "ingested",
      })
      row += 1
    }
    nodes.push({
      id: `warehouse-${warehouse.id}`,
      type: "warehouse",
      position: {
        x: WAREHOUSE_X,
        y: (group.length === 0 ? row : (firstRow + row - 1) / 2) * ROW_HEIGHT,
      },
      data: { name: warehouse.name, storeCount: group.length },
    })
    row += 1
  }

  const unroutedStores = graph.stores.filter((store) => !routeByStore.has(store.id))
  if (unroutedStores.length > 0) {
    row += UNROUTED_GAP
    for (const store of unroutedStores) {
      nodes.push(storeNode(store, null))
      row += 1
    }
  }

  return { nodes, edges }
}

export default function SetupRoutes() {
  const { graph, isLoading } = useRouteGraph()
  const { nodes, edges } = useMemo(
    () => (graph ? buildGraph(graph) : { nodes: [], edges: [] }),
    [graph],
  )

  if (isLoading) {
    return <div className="paragraph-sm p-4 text-fg-muted">Loading store sourcing</div>
  }

  if (!graph || nodes.length === 0) {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center rounded-lg border border-base bg-base">
        <div className="max-w-100 text-center">
          <p className="label-base text-fg-base">No retail locations yet</p>
          <p className="paragraph-sm mt-1 text-fg-subtle">
            Warehouses and stores arrive with the retail location load. So does the warehouse each
            store is sourced from.
          </p>
        </div>
      </div>
    )
  }

  const routedStores = new Set(graph.routes.map((route) => route.storeId)).size
  const unroutedStores = graph.stores.length - routedStores
  const unroutedWarehouses =
    graph.warehouses.length - new Set(graph.routes.map((route) => route.warehouseId)).size

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2">
      <div className="paragraph-xs text-fg-subtle">
        {routedStores} store{routedStores === 1 ? "" : "s"} sourced from {graph.warehouses.length}{" "}
        warehouse{graph.warehouses.length === 1 ? "" : "s"} · animated links are ingested
        {(unroutedStores > 0 || unroutedWarehouses > 0) && (
          <span className="text-badge-orange-fg-text">
            {" "}
            · {unroutedStores} store{unroutedStores === 1 ? "" : "s"} and {unroutedWarehouses}{" "}
            warehouse{unroutedWarehouses === 1 ? "" : "s"} unmapped
          </span>
        )}
      </div>
      <div className="min-h-0 flex-1 overflow-hidden rounded-lg border border-base bg-base">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          colorMode="system"
          fitView
          nodesDraggable={false}
          nodesConnectable={false}
        >
          <Background />
          <Controls showInteractive={false} />
          <MiniMap pannable zoomable />
        </ReactFlow>
      </div>
    </div>
  )
}
